import { FormEvent, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { track } from '@/lib/analytics';
import { Memory } from '@/types';
import { MEMORY } from '@/content/memory';
type State = 'idle' | 'loading' | 'success' | 'queued' | 'error';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

declare global { interface Window { turnstile?: any; } }

interface Props { source: string; onLeft?: (m: Memory) => void; }

const MemoryForm = ({ source, onLeft }: Props) => {
  const [text, setText] = useState('');
  const [initials, setInitials] = useState('');
  const [state, setState] = useState<State>('idle');
  const [error, setError] = useState('');
  const tsRef = useRef<HTMLDivElement>(null);
  const tokenRef = useRef<string>('');
  const renderedRef = useRef(false);

  useEffect(() => {
    if (!SITE_KEY || !tsRef.current || !window.turnstile || renderedRef.current) return;
    renderedRef.current = true;
    const id = window.turnstile.render(tsRef.current, {
      sitekey: SITE_KEY, size: 'flexible',
      callback: (t: string) => { tokenRef.current = t; },
    });
    return () => { renderedRef.current = false; try { window.turnstile.remove(id); } catch { /* noop */ } };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length < 2 || trimmed.length > 150) { setState('error'); setError('2–150 characters.'); return; }
    if (SITE_KEY && !tokenRef.current) {
      setState('error'); setError('Please wait for the security check to finish.');
      return;
    }
    setState('loading'); setError('');
    const res = await api.leaveMemory({ text: trimmed, initials: initials.trim() || undefined, turnstileToken: tokenRef.current, source });
    if (res.error || !res.data) { setState('error'); setError(res.error || MEMORY.rejected); return; }
    track('memory_left', { source });
    onLeft?.(res.data);
    setState('success'); setText(''); setInitials('');
  };

  if (state === 'success' || state === 'queued') {
    return <p className="mw-form-done" role="status">{MEMORY.successHeading}</p>;
  }

  return (
    <form className="mw-form" onSubmit={submit} noValidate>
      <div className="mw-form-row">
        <input className="mw-form-text" value={text} maxLength={150}
          onChange={(e) => { setText(e.target.value); if (state === 'error') setState('idle'); }}
          placeholder={MEMORY.placeholder} aria-label="Your memory" disabled={state === 'loading'} />
        <input className="mw-form-ini" value={initials} maxLength={4}
          onChange={(e) => setInitials(e.target.value)} placeholder={MEMORY.initialsPlaceholder}
          aria-label="Your initials (optional)" disabled={state === 'loading'} />
        <button className="mw-form-btn" type="submit" disabled={state === 'loading'}>
          {state === 'loading' ? MEMORY.submitting : MEMORY.submit}
        </button>
      </div>
      {SITE_KEY && <div ref={tsRef} className="mw-form-ts" />}
      {state === 'error' && <p className="mw-form-err" role="alert">{error}</p>}

      <style>{`
        .mw-form-row { display:flex; gap:1rem; align-items:stretch; flex-wrap:wrap; }
        .mw-form-text { flex:1; min-width:200px; background:transparent; border:none;
          border-bottom:1px solid rgba(232,236,255,.4); color:var(--et-text);
          font-family:var(--font-family); font-size:1.05rem; padding:.7rem 0; outline:none; caret-color:var(--et-cyan); }
        .mw-form-text:focus { border-color:var(--et-cyan); box-shadow:0 1px 0 0 var(--et-cyan); }
        .mw-form-ini { width:84px; background:transparent; border:none; border-bottom:1px solid rgba(232,236,255,.4);
          color:var(--et-text); font-family:var(--font-mono); padding:.7rem 0; outline:none; }
        .mw-form-btn { padding:.85rem 1.7rem; background:#fff; border:1px solid #fff; color:#05060f;
          font-family:var(--font-mono); font-size:.72rem; font-weight:700; letter-spacing:.26em; cursor:pointer; white-space:nowrap; }
        .mw-form-btn:hover:not(:disabled) { background:transparent; color:#fff; }
        .mw-form-btn:disabled { opacity:.6; cursor:wait; }
        .mw-form-ts { margin-top:1rem; }
        .mw-form-err { margin:.8rem 0 0; font-family:var(--font-mono); font-size:.72rem; color:#ef4444; }
        .mw-form-done { margin:0; font-family:var(--font-display); font-weight:700; text-transform:uppercase;
          font-size:clamp(1.5rem,3.4vw,2.4rem); color:var(--et-text); }
        @media (max-width:560px){ .mw-form-btn{ align-self:flex-start; } }
      `}</style>
    </form>
  );
};

export default MemoryForm;
